import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { FadeIn, MaskedLine } from "@/components/shared/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interestOptions = [
  "Legacy Assessment",
  "Re-platform / Rebuild",
  "ServiceNow Development",
  "Workflow Automation",
  "Integrations / APIs",
  "AI-Assisted Modernization",
];

const inputCls =
  "w-full bg-ink border hairline px-4 py-3.5 text-slate-100 placeholder:text-slate-600 font-light focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-slate-950 transition-[border-color] duration-300";

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    legacy_systems: "",
    message: "",
  });
  const [interests, setInterests] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggle = (opt) =>
    setInterests((prev) =>
      prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt]
    );

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/assessments`, { ...form, interests });
      setSubmitted(true);
      toast.success("Assessment request received.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-ink">
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute -top-32 right-0 w-[520px] h-[520px] rounded-full bg-amber-600/10 blur-3xl pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <MaskedLine delay={0.1}>
            <p className="label-mono">Contact / Legacy Application Assessment</p>
          </MaskedLine>
          <h1 className="mt-8 font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-slate-100">
            <MaskedLine delay={0.25}>Tell us about</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="italic text-gold">your legacy estate.</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.6}>
            <p className="mt-8 max-w-xl text-lg text-slate-400 font-light leading-relaxed">
              A candid conversation about your applications, your options, and the path we
              would take. No sales script, no predetermined technology recommendation.
            </p>
          </MaskedLine>
        </div>
      </section>

      <section className="border-t hairline bg-ink-light">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <FadeIn className="lg:col-span-4">
            <p className="label-mono mb-6">What happens next</p>
            <ol className="space-y-8">
              {[
                ["01", "We read every word. A person — the founder — responds, not an autoresponder."],
                ["02", "A working conversation about your systems, your people, and your constraints."],
                ["03", "An honest assessment of options. If we are not the right fit, we will say so."],
              ].map(([n, t]) => (
                <li key={n} className="flex gap-5">
                  <span className="font-mono-tech text-amber-500/70 text-sm tracking-[0.2em] shrink-0">{n}</span>
                  <p className="text-slate-400 font-light leading-relaxed text-sm">{t}</p>
                </li>
              ))}
            </ol>
          </FadeIn>

          <FadeIn delay={0.1} className="lg:col-span-8">
            <div className="border hairline bg-ink p-8 md:p-12">
              {submitted ? (
                <div data-testid="contact-success" className="py-16 text-center">
                  <CheckCircle2 className="mx-auto text-gold mb-6" size={44} strokeWidth={1.5} />
                  <h2 className="font-serif text-3xl md:text-4xl text-slate-100 mb-4">
                    Request received.
                  </h2>
                  <p className="text-slate-400 font-light max-w-md mx-auto leading-relaxed">
                    Thank you for reaching out. We will review your legacy landscape and respond
                    personally — usually within one business day.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="assessment-form" className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="label-mono block mb-3">Name *</label>
                      <input
                        id="name"
                        data-testid="form-name-input"
                        required
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Your full name"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="label-mono block mb-3">Email *</label>
                      <input
                        id="email"
                        type="email"
                        data-testid="form-email-input"
                        required
                        value={form.email}
                        onChange={set("email")}
                        placeholder="you@company.com"
                        className={inputCls}
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="company" className="label-mono block mb-3">Company</label>
                    <input
                      id="company"
                      data-testid="form-company-input"
                      value={form.company}
                      onChange={set("company")}
                      placeholder="Organization name"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="legacy_systems" className="label-mono block mb-3">Legacy Systems</label>
                    <input
                      id="legacy_systems"
                      data-testid="form-legacy-input"
                      value={form.legacy_systems}
                      onChange={set("legacy_systems")}
                      placeholder="e.g., COBOL/DB2 mainframe, aging .NET apps, on-prem ERP"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <p className="label-mono mb-4">Areas of Interest</p>
                    <div className="flex flex-wrap gap-3" data-testid="form-interests-group">
                      {interestOptions.map((opt) => (
                        <button
                          type="button"
                          key={opt}
                          data-testid={`form-interest-${opt.toLowerCase().replace(/[\s/]+/g, "-")}`}
                          onClick={() => toggle(opt)}
                          className={`font-mono-tech text-[11px] tracking-[0.15em] uppercase px-4 py-2.5 border transition-[background-color,color,border-color] duration-300 ${
                            interests.includes(opt)
                              ? "bg-amber-400 text-ink border-amber-400"
                              : "border-amber-800/50 text-slate-400 hover:text-amber-400 hover:border-amber-600/60"
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="message" className="label-mono block mb-3">Your Situation</label>
                    <textarea
                      id="message"
                      data-testid="form-message-input"
                      rows={5}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="What is working, what is not, and what prompted you to reach out now?"
                      className={`${inputCls} resize-none`}
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="form-submit-button"
                    disabled={submitting}
                    className="group inline-flex items-center gap-3 bg-amber-400 text-ink px-8 py-4 font-mono-tech text-xs tracking-[0.22em] uppercase transition-[background-color,opacity] duration-300 hover:bg-amber-300 disabled:opacity-50"
                  >
                    {submitting ? "Sending..." : "Request Assessment"}
                    <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </button>
                </form>
              )}
            </div>
          </FadeIn>
        </div>
      </section>
    </main>
  );
};

export default Contact;
